```vue
<script setup lang="ts">
import { ref, computed } from 'vue';
import { useCategoryStore } from '../../stores';
import type { ProductWithDetails } from '../../types';

const props = defineProps<{
  show: boolean;
  availableProducts: ProductWithDetails[];
  selectedLaboratoryId: string;
  initialSelection?: string[];
  currentProductId?: string;
  productName?: string; // Add prop for product name
  laboratoryName?: string; // Add prop for laboratory name
}>();

const emit = defineEmits<{
  'close': [];
  'save': [string[]];
}>();

const categoryStore = useCategoryStore();
const searchTerm = ref('');
const selectedProducts = ref<string[]>(props.initialSelection || []);
const selectedCategory = ref('');

// Filtrar productos disponibles
const filteredProducts = computed(() => {
  return props.availableProducts.filter(product => {
    // No mostrar el producto actual
    if (product.id === props.currentProductId) return false;

    // No mostrar productos que ya son compuestos
    if (product.laboratories.some(lab => lab.details.isCompuesto)) return false;

    // Filtrar por término de búsqueda
    if (searchTerm.value && !product.Nombre.toLowerCase().includes(searchTerm.value.toLowerCase())) {
      return false;
    }

    // Filtrar por categoría
    if (selectedCategory.value && product.Categoria !== selectedCategory.value) {
      return false;
    }

    // Solo mostrar productos del mismo laboratorio
    return product.laboratories.some(lab => lab.laboratory.id === props.selectedLaboratoryId);
  });
});

// Verificar si un producto está seleccionado
const isSelected = (productId: string) => {
  return selectedProducts.value.includes(productId);
};

// Alternar selección de producto
const toggleProduct = (productId: string) => {
  if (isSelected(productId)) {
    selectedProducts.value = selectedProducts.value.filter(id => id !== productId);
  } else {
    selectedProducts.value.push(productId);
  }
};

// Calcular precio total de los componentes
const calculateTotalPrice = (productId: string) => {
  const product = props.availableProducts.find(p => p.id === productId);
  if (!product) return 0;

  const lab = product.laboratories.find(l => l.laboratory.id === props.selectedLaboratoryId);
  return lab?.details.PrecioDeCosto || 0;
};

// Guardar selección
const handleSave = () => {
  emit('save', selectedProducts.value);
};

// Limpiar filtros
const clearFilters = () => {
  searchTerm.value = '';
  selectedCategory.value = '';
};
</script>

<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex min-h-screen items-center justify-center p-4">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="emit('close')" />
      
      <!-- Modal -->
      <div class="relative bg-white rounded-lg shadow-xl w-[95%] md:w-[85%] lg:w-[70%] mx-auto">
        <div class="flex items-center justify-between p-4 border-b">
          <div>
            <h3 class="text-xl font-semibold text-slate-800">
              Seleccionar Componentes
            </h3>
            <p class="text-sm text-slate-600 mt-1">
              {{ productName }} - {{ laboratoryName }}
            </p>
          </div>
          <button @click="emit('close')" class="text-slate-400 hover:text-slate-500">
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="p-6">
          <!-- Filtros -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <!-- Buscador -->
            <div class="relative">
              <input
                v-model="searchTerm"
                type="text"
                class="input-field pl-10"
                placeholder="Buscar productos..."
              />
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <!-- Selector de categoría -->
            <div>
              <select v-model="selectedCategory" class="input-field">
                <option value="">Todas las categorías</option>
                <option v-for="[id, label] in Object.entries(categoryStore.categories)" :key="id" :value="id">
                  {{ label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Botón limpiar filtros -->
          <div v-if="searchTerm || selectedCategory" class="flex justify-end mb-4">
            <button 
              @click="clearFilters"
              class="text-sm text-slate-600 hover:text-slate-800 flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Limpiar filtros
            </button>
          </div>

          <!-- Lista de productos -->
          <div class="border rounded-lg overflow-hidden">
            <div class="max-h-96 overflow-y-auto">
              <table class="w-full border-collapse">
                <thead class="bg-slate-50 sticky top-0">
                  <tr>
                    <th class="w-8 p-3 border-b"></th>
                    <th class="text-left p-3 border-b">Nombre</th>
                    <th class="text-left p-3 border-b">Categoría</th>
                    <th class="text-right p-3 border-b">Precio</th>
                  </tr>
                </thead>
                <tbody class="divide-y">
                  <tr v-for="product in filteredProducts" 
                      :key="product.id"
                      class="hover:bg-slate-50"
                  >
                    <td class="p-3 text-center">
                      <input
                        type="checkbox"
                        :checked="isSelected(product.id!)"
                        @change="toggleProduct(product.id!)"
                        class="rounded text-indigo-600 focus:ring-indigo-500"
                      />
                    </td>
                    <td class="p-3">{{ product.Nombre }}</td>
                    <td class="p-3 text-slate-600">{{ categoryStore.categories[product.Categoria] }}</td>
                    <td class="p-3 text-right">
                      ${{ calculateTotalPrice(product.id!).toLocaleString() }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Resumen -->
          <div class="mt-6 bg-slate-50 p-4 rounded-lg">
            <div class="flex justify-between items-center">
              <div>
                <span class="text-sm text-slate-600">
                  {{ selectedProducts.length }} componentes seleccionados
                </span>
              </div>
              <div class="text-right">
                <div class="text-sm text-slate-600">Precio Total:</div>
                <div class="text-lg font-medium text-slate-900">
                  ${{ selectedProducts
                    .reduce((total, id) => total + calculateTotalPrice(id), 0)
                    .toLocaleString() }}
                </div>
              </div>
            </div>
          </div>

          <!-- Botones -->
          <div class="flex justify-end gap-4 mt-6">
            <button
              @click="emit('close')"
              class="px-4 py-2 text-slate-600 hover:text-slate-800"
            >
              Cancelar
            </button>
            <button
              @click="handleSave"
              class="btn-primary"
              :disabled="selectedProducts.length === 0"
            >
              Guardar Selección
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.input-field {
  @apply w-full p-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 
         focus:border-indigo-500 outline-none transition-all duration-200;
}
</style>
```